// components
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import CreateButton from "components/buttons/CreateButton";
import CustomerCard from "components/card/CustomerCard";
import { getPaginatedCustomers, getCustomerTags } from "interfaces/customer";
import { getDeliveryTours } from "interfaces/deliveryTour";
import LoadingCard from "./components/loadingCard";
import Nft4 from "assets/img/nfts/Nft4.png";
import SearchIcon from "components/icons/SearchIcon";
import Card from "components/card";
import Pagination from "components/pagination/Pagination";
import OnBoardingCard from "views/admin/main/account/coursePage/components/OnBoardingCard";
import { useShepherdTour } from "react-shepherd";
import CRMMenu from "./components/CRMMenu";
import { fetchFilter, setFilter } from "utils/filtersStorage";
import { getUsersLight } from "interfaces/user";
import { MdViewModule, MdTableRows, MdViewColumn } from "react-icons/md";
import { differenceInCalendarDays, parseISO, isValid as isValidDateFn } from 'date-fns';
import TagMultiSelect from "./components/selectTags";
import { useRef } from "react";

const steps: any = [
    {
        title: "4/5 Personnaliser les prix",
        text: "Cliquer sur un client, puis sur 'gérer les prix'",
        attachTo: {
            element: ".step-customer-0",
            on: "top",
        },
        buttons: [
            {
                classes: "shepherd-button-primary",
                text: "J'ai compris !",
                type: "next",
            },
        ],
        id: "step-customer-0",
    },
];

const secondSteps: any = [
    {
        title: "5/5 Partager la boutique B2B",
        text: "Cliquer sur un client, puis sur 'envoyer la page de commande'. Vous pouvez maintenant découvrir la page de commande puis l'envoyer à vos clients.",
        attachTo: {
            element: ".step-customer-0",
            on: "top",
        },
        buttons: [
            {
                classes: "shepherd-button-primary",
                text: "J'ai compris !",
                type: "next",
            },
        ],
        id: "step-customer-0",
    },
];

const CRM = () => {
    const nav = useNavigate();
    const [customers, setCustomers] = useState(null);
    const [customersMeta, setCustomersMeta] = useState(null);
    const [search, setSearch] = useState('');
    const [firstFetch, setFirstFetch] = useState(true);
    const [initSearch, setInitSearch] = useState(true);
    const [page, setPage] = useState(1);
    const [limitPerPage, setLimitPerPage] = useState(12);
    const [onboardingStep, setOnboardingStep] = useState(null);
    const isFullyLoaded = customers;
    const [view, setView] = useState<'grid' | 'table'>(fetchFilter('customers')?.view || 'grid');
    const [assignedTo, setAssignedTo] = useState<string>(fetchFilter('customers')?.assignedTo || '');
    const [tags, setTags] = useState<string[]>(Array.isArray(fetchFilter('customers')?.tags) ? fetchFilter('customers')?.tags : []);
    const [deliveryTourId, setDeliveryTourId] = useState<string>(fetchFilter('customers')?.deliveryTourId || '');
    const [users, setUsers] = useState<any[]>([]);
    const [tagOptions, setTagOptions] = useState<string[]>([]);
    const [deliveryTours, setDeliveryTours] = useState<any[]>([]);
    const [visibleColumns, setVisibleColumns] = useState<string[]>(
        JSON.parse(localStorage.getItem('customers-visible-columns') || '[]').length > 0
            ? JSON.parse(localStorage.getItem('customers-visible-columns') || '[]')
            : ['company', 'contact', 'email', 'phone', 'assignedTo', 'tags', 'lastOrder']
    );
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [showColumnSelector, setShowColumnSelector] = useState(false);
    const columnSelectorRef = useRef<HTMLDivElement>(null);

    const tourOptions = {
        defaultStepOptions: {
            cancelIcon: {
                enabled: true,
            },
        },
        useModalOverlay: true,
    };
    const tour = useShepherdTour({ tourOptions, steps });
    const secondtour = useShepherdTour({ tourOptions, steps: secondSteps });

    const availableColumns = [
        { id: 'company', label: 'Entreprise', required: true },
        { id: 'contact', label: 'Contact', required: false },
        { id: 'email', label: 'Email', required: false },
        { id: 'phone', label: 'Téléphone', required: false },
        { id: 'assignedTo', label: 'Assigné à', required: false },
        { id: 'tags', label: 'Tags', required: false },
        { id: 'billingPeriod', label: 'Période de facturation', required: false },
        { id: 'leftToPay', label: 'Reste à payer', required: false },
        { id: 'lastOrder', label: 'Dernière commande', required: false },
    ];

    const toggleColumn = (columnId: string) => {
        const column = availableColumns.find(col => col.id === columnId);
        if (column?.required) return;

        const newVisibleColumns = visibleColumns.includes(columnId)
            ? visibleColumns.filter(id => id !== columnId)
            : [...visibleColumns, columnId];

        setVisibleColumns(newVisibleColumns);
        localStorage.setItem('customers-visible-columns', JSON.stringify(newVisibleColumns));
    };

    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const sortCustomers = (customersList: any[]) => {
        if (!sortColumn || !customersList) return customersList;

        return [...customersList].sort((a, b) => {
            let aValue: any;
            let bValue: any;

            if (sortColumn === 'company') {
                aValue = (a.company || a.displayName || '').toLowerCase();
                bValue = (b.company || b.displayName || '').toLowerCase();
            } else if (sortColumn === 'contact') {
                aValue = (a.contact || '').toLowerCase();
                bValue = (b.contact || '').toLowerCase();
            } else if (sortColumn === 'email') {
                aValue = (a.mail || '').toLowerCase();
                bValue = (b.mail || '').toLowerCase();
            } else if (sortColumn === 'phone') {
                aValue = (a.phone || '').toLowerCase();
                bValue = (b.phone || '').toLowerCase();
            } else if (sortColumn === 'assignedTo') {
                aValue = getAssignedToName(a).toLowerCase();
                bValue = getAssignedToName(b).toLowerCase();
            } else if (sortColumn === 'tags') {
                aValue = ((a.tags || a.tag || [])?.toString() || '').toLowerCase();
                bValue = ((b.tags || b.tag || [])?.toString() || '').toLowerCase();
            } else if (sortColumn === 'billingPeriod') {
                aValue = (a.billingPeriod || '').toLowerCase();
                bValue = (b.billingPeriod || '').toLowerCase();
            } else if (sortColumn === 'leftToPay') {
                aValue = Number(a.leftToPay || 0);
                bValue = Number(b.leftToPay || 0);
            } else if (sortColumn === 'lastOrder') {
                const aDate = a?.lastOrdersDates;
                const bDate = b?.lastOrdersDates;
                aValue = aDate ? new Date(aDate).getTime() : 0;
                bValue = bDate ? new Date(bDate).getTime() : 0;
            }

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortDirection === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const fetchCustomers = async (page: number, limit: number) => {
        const cleanTags = tags.filter(Boolean).map(t => t.trim());
        
        const res = await getPaginatedCustomers(page, limit, search, { 
          assignedTo: assignedTo || undefined, 
          tags: cleanTags.length ? cleanTags : undefined,
          deliveryTourId: deliveryTourId || undefined,
        });
      
        if (res) {
          setCustomers(res.data);
          setCustomersMeta(res.meta);
          setPage(page);
          setLimitPerPage(limit);
      
          if (firstFetch) setFirstFetch(false);
      
          if (!tagOptions.length) {
            const set = new Set<string>();
            (res.data || []).forEach((c: any) => {
              const raw = c?.tags ?? [];
              const arr = Array.isArray(raw) ? raw : String(raw || '').split(',');
              arr
                .map((t: any) => (typeof t === 'string' ? t : t?.name))
                .filter(Boolean)
                .forEach((t: string) => set.add(t.trim()));
            });
            if (set.size) setTagOptions(Array.from(set).sort());
          }
        }
      };
      

    const getLastOrderDaysAgo = (entry: any): string => {
            const raw = entry?.lastOrdersDates;
            if (!raw) return '-';
        
            const toDate = (v: any): Date | null => {
              if (!v) return null;
              if (v instanceof Date && !isNaN(v.getTime())) return v;
              if (typeof v === 'string') {
                const t = v.trim();
                if (!t) return null;
                const d = parseISO(t);
                if (isValidDateFn(d)) return d;
                const d2 = new Date(t);
                return isNaN(d2.getTime()) ? null : d2;
              }
              const d3 = new Date(v);
              return isNaN(d3.getTime()) ? null : d3;
            };
        
            let dates: Date[] = [];
            if (Array.isArray(raw)) {
              dates = raw.map(toDate).filter(Boolean) as Date[];
            } else if (typeof raw === 'string') {
              dates = raw
                .split(',')
                .map((s) => toDate(s))
                .filter(Boolean) as Date[];
              // si chaîne unique sans virgule, ci-dessus gère aussi
            } else {
              const d = toDate(raw);
              if (d) dates = [d];
            }
        
            if (!dates.length) return '-';
            const latest = dates.reduce((max, d) => (d.getTime() > max.getTime() ? d : max), dates[0]);
            const days = differenceInCalendarDays(new Date(), latest);
            return `il y a ${days} jour${days > 1 ? 's' : ''}`;
          };

    const usersById = useMemo(() => {
        const m = new Map<string, string>();
            users?.forEach((u: any) => {
        const id = u?._id || u?.id;
        const label = u?.fullName || u?.name || u?.email || 'Utilisateur';
        if (id) m.set(String(id), label);
      });
    return m;
    }, [users]);

 const getAssignedToName = (c: any): string => {
   // Priorité: objet peuplé depuis l’API, sinon mapping via id, sinon fallback
   const populated = c?.fullName;
   if (populated) return populated;

   const raw = c?.assignedTo ?? [];
  const ids: string[] = Array.isArray(raw)
    ? raw.map((v: any) =>
        typeof v === 'string'
          ? v
          : (v?._id || String(v))
      )
    : [typeof raw === 'string' ? raw : raw?._id ].filter(Boolean);

    const labels = ids
    .filter(Boolean)
    .map((id) => usersById.get(String(id)))
    .filter(Boolean) as string[];

  if (labels.length === 0) return '-';

  return labels.length <= 2 ? labels.join(', ') : `${labels[0]}, ${labels[1]} +${labels.length - 2}`;
 };

    const OnboardingOptions = () => {
        return (
            <>
                {onboardingStep === "3" && (
                    <div className="mt-5 mb-4">
                        <OnBoardingCard
                            title="Importer vos clients"
                            actualStep={3}
                            totalStep={5}
                            description="Vous pouvez import vos clients depuis un fichier CSV ou les ajouter manuellement."
                            onClick={() => {
                                setOnboardingStep("4");
                                localStorage.setItem("onboarding-step", "4");
                                tour.start();
                            }}
                        />
                    </div>
                )}
                {onboardingStep === "4" && (
                    <div className="mt-5 mb-4">
                        <OnBoardingCard
                            title="Personnaliser les prix"
                            actualStep={4}
                            totalStep={5}
                            description="Si nécessaire, vous pouvez maintenant personnaliser les prix de vos produits par client."
                            onClick={() => {
                                setOnboardingStep("5");
                                localStorage.setItem("onboarding-step", "5");
                                secondtour.start();
                            }}
                        />
                    </div>
                )}
                {onboardingStep === "5" && (
                    <div className="mt-5 mb-4">
                        <OnBoardingCard
                            title="Partager la boutique B2B"
                            actualStep={5}
                            totalStep={5}
                            description="Vous pouvez maintenant découvrir la page de commande puis l'envoyer à vos clients."
                            nextText="Terminer !"
                            onClick={() => {
                                setOnboardingStep(null);
                                localStorage.setItem("onboarding-step", "1000");
                            }}
                        />
                    </div>
                )}
            </>
        );
    };

    useEffect(() => {
        if (localStorage.getItem("token") === null) {
            nav("/login");
        }
        // First fetch handling cached filters
        const filters = fetchFilter('customers');
        if (filters) {
            setSearch(filters.search)
            setAssignedTo(filters.assignedTo || '');
            setTags(filters.tags || []);
            setDeliveryTourId(filters.deliveryTourId || '');
            setView(filters.view || 'grid');
        } else {
            fetchCustomers(filters?.page || page, filters?.limit || limitPerPage);
        }
        (async () => {
                 const u = await getUsersLight();
                 if (u) setUsers(u);
                 const t = await getCustomerTags();
                 if (t?.length) setTagOptions(t);
                 const tours = await getDeliveryTours(false);
                 if (tours?.data) setDeliveryTours(tours.data);
               })();
        setOnboardingStep(localStorage.getItem("onboarding-step"));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!firstFetch)
        setFilter("customers", { page, limit: limitPerPage, search, assignedTo, tags, deliveryTourId, view });
    }, [page, limitPerPage, search, assignedTo, tags, deliveryTourId, view, firstFetch]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (columnSelectorRef.current && !columnSelectorRef.current.contains(event.target as Node)) {
                setShowColumnSelector(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {

        // If init (search = '') but cached search is not empty,return
        const filters = fetchFilter('customers');
        if (initSearch && filters?.search !== '') {
            setInitSearch(false);
            return;
        }

        // Handle the first fetch with cached filters if exists
        if (firstFetch) {
            const filters = fetchFilter("customers");
            if (filters) {
                fetchCustomers(
                    filters?.page || page,
                    filters?.limit || limitPerPage
                );
            }
        } else {
            // Handle normal search
            setCustomers(null);
            const delayDebounceFn = setTimeout(() => {
                fetchCustomers(1, limitPerPage);
            }, 800);
            return () => clearTimeout(delayDebounceFn);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, assignedTo, tags, deliveryTourId]);

    return (
        <>
            <OnboardingOptions />

            <CRMMenu path="/customers" />
            <Card extra="mt-2 pt-[10px] pl-[20px] pr-[20px] pb-[20px]">
                <div className="mt-4 grid h-full w-full grid-cols-1 gap-5 xl:mt-3">
                    <div className="h-full w-full rounded-[20px]">
                    <div className="mt-3 flex justify-between items-center gap-3 flex-col sm:flex-row">
                            <div className="flex h-[38px] items-center rounded-xl bg-lightPrimary text-sm text-gray-600 dark:!bg-navy-900 dark:text-white w-full sm:w-[50%] lg:w-[40%]">
                                <SearchIcon />
                                <input
                                    type="text"
                                    placeholder={"Entreprise, mail, téléphone, contact, ...."}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="block w-full rounded-full bg-lightPrimary text-base text-navy-700 outline-none dark:!bg-navy-900 dark:text-white"
                                />
                            </div>
                            <div className="flex gap-2">
                                 <CreateButton
                                     value="Ajouter un client"
                                     extra="my-auto h-10 col-start-1 col-span-6 md:col-span-4 lg:col-start-8 lg:col-span-3 3xl:col-start-9 3xl:col-span-2"
                                     onClick={() => nav("/create-customer")}
                                 />
                                 <CreateButton
                                     value="Import CSV"
                                     extra="my-auto h-10 col-start-1 col-span-6 md:col-span-4 lg:col-start-8 lg:col-span-3 3xl:col-start-9 3xl:col-span-2"
                                     onClick={() => nav("/import-customer")}
                                 />
                             </div>
                         </div>
                         <div className="mt-3 flex items-center justify-between gap-3 flex-wrap rounded-md border border-gray-200 bg-white px-3 py-2 dark:!bg-navy-800 dark:border-navy-700">
                            <div className="flex items-center gap-3 flex-wrap">
                                {/* Filtre assignedTo compact */}
                                <div className="relative">
                                    <label className="text-[11px] text-gray-500 mr-2 hidden sm:inline">Chargé de compte</label>
                                    <select
                                        value={assignedTo}
                                        onChange={(e) => setAssignedTo(e.target.value)}
                                        className="w-44 sm:w-56 max-w-[220px] rounded-md border border-gray-300 px-2 py-1 h-8 text-xs focus:border-brand-500 outline-none bg-white pr-6"
                                        disabled={!users?.length}
                                    >
                                        <option value="">Tous</option>
                                        {users.map((u) => (
                                            <option key={u._id} value={u._id} title={u.email || u.fullName}>
                                                {u.fullName} {u.email ? `(${u.email})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                    {assignedTo && (
                                        <button
                                            type="button"
                                            onClick={() => setAssignedTo('')}
                                            className="absolute right-1 top-1.5 p-0.5 text-gray-400 hover:text-gray-600"
                                            title="Effacer"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>

                                <TagMultiSelect
                                    options={tagOptions}
                                    values={tags}
                                    onChange={setTags}
                                    label="Tags"
                                    maxChips={3}
                                />

                                {/* Filtre tournée de livraison */}
                                <div className="relative">
                                    <label className="text-[11px] text-gray-500 mr-2 hidden sm:inline">Tournée</label>
                                    <select
                                        value={deliveryTourId}
                                        onChange={(e) => setDeliveryTourId(e.target.value)}
                                        className="w-44 sm:w-56 max-w-[220px] rounded-md border border-gray-300 px-2 py-1 h-8 text-xs focus:border-brand-500 outline-none bg-white pr-6"
                                        disabled={!deliveryTours?.length}
                                    >
                                        <option value="">Toutes</option>
                                        {deliveryTours.map((tour) => (
                                            <option key={tour._id} value={tour._id}>
                                                {tour.name}
                                            </option>
                                        ))}
                                    </select>
                                    {deliveryTourId && (
                                        <button
                                            type="button"
                                            onClick={() => setDeliveryTourId('')}
                                            className="absolute right-1 top-1.5 p-0.5 text-gray-400 hover:text-gray-600"
                                            title="Effacer"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Toggle vue et colonnes */}
                            <div className="flex items-center gap-2 ml-auto">
                                {view === 'table' && (
                                    <div className="relative" ref={columnSelectorRef}>
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setShowColumnSelector(!showColumnSelector);
                                            }}
                                            className="flex items-center gap-1 px-2 h-8 text-xs border border-gray-300 rounded hover:bg-gray-50 transition-colors text-gray-700"
                                            title="Personnaliser les colonnes"
                                        >
                                            <MdViewColumn size={16} />
                                            <span>Colonnes</span>
                                        </button>
                                        {showColumnSelector && (
                                            <div
                                                className="absolute right-0 top-full mt-1 z-[9999] bg-white border border-gray-300 rounded-md shadow-xl p-3 min-w-[200px]"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <p className="text-xs font-semibold text-gray-700 mb-2 pb-2 border-b">Colonnes visibles</p>
                                                <div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto">
                                                    {availableColumns.map((column) => (
                                                        <label
                                                            key={column.id}
                                                            className={`flex items-center gap-2 text-sm hover:bg-gray-50 p-1.5 rounded ${column.required ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={visibleColumns.includes(column.id)}
                                                                onChange={() => {
                                                                    if (!column.required) {
                                                                        toggleColumn(column.id);
                                                                    }
                                                                }}
                                                                disabled={column.required}
                                                                className={`w-4 h-4 ${column.required ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                                            />
                                                            <span>{column.label}</span>
                                                            {column.required && <span className="text-xs text-gray-400">(requis)</span>}
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <button
                                    className={`h-8 w-8 flex items-center justify-center rounded-md border ${view === 'grid' ? 'bg-toola-50 text-toola-600 border-toola-300' : 'bg-white text-gray-500 border-gray-300'}`}
                                    title="Vue cartes"
                                    onClick={() => setView('grid')}
                                >
                                    <MdViewModule />
                                </button>
                                <button
                                    className={`h-8 w-8 flex items-center justify-center rounded-md border ${view === 'table' ? 'bg-toola-50 text-toola-600 border-toola-300' : 'bg-white text-gray-500 border-gray-300'}`}
                                    title="Vue tableau"
                                    onClick={() => setView('table')}
                                >
                                    <MdTableRows />
                                </button>
                            </div>
                        </div>
                        {view === 'grid' && (
             <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 2xl:grid-cols-4">
               {isFullyLoaded && customers.length === 0 }
               {isFullyLoaded
                 ? customers.map((customer: any, index: number) => (
                     <div className={`step-customer-${index}`} key={customer._id}>
                       <CustomerCard
                         id={customer._id}
                         company={customer.company}
                         displayName={customer.displayName}
                         email={customer.mail}
                         phone={customer.phone}
                         commands="0"
                         image={Nft4}
                         customerId={customer.customerId}
                       />
                     </div>
                   ))
                 : Array(8).fill(0).map((_, i) => <LoadingCard key={i} />)}
             </div>
           )}

           {/* Vue: TABLE */}
           {view === 'table' && (
             <div className="mt-8 overflow-x-auto rounded-xl border border-gray-200">
               <table className="min-w-full text-sm text-gray-700">
                 <thead className="bg-gray-50 text-gray-600">
                   <tr>
                     {visibleColumns.includes('company') && (
                       <th 
                         className="text-left py-3 px-4 cursor-pointer hover:bg-gray-100 select-none"
                         onClick={() => handleSort('company')}
                       >
                         <div className="flex items-center gap-1">
                           Entreprise
                           {sortColumn === 'company' && (
                             <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                           )}
                         </div>
                       </th>
                     )}
                     {visibleColumns.includes('contact') && (
                       <th 
                         className="text-left py-3 px-4 cursor-pointer hover:bg-gray-100 select-none"
                         onClick={() => handleSort('contact')}
                       >
                         <div className="flex items-center gap-1">
                           Contact
                           {sortColumn === 'contact' && (
                             <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                           )}
                         </div>
                       </th>
                     )}
                     {visibleColumns.includes('email') && (
                       <th 
                         className="text-left py-3 px-4 cursor-pointer hover:bg-gray-100 select-none"
                         onClick={() => handleSort('email')}
                       >
                         <div className="flex items-center gap-1">
                           Email
                           {sortColumn === 'email' && (
                             <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                           )}
                         </div>
                       </th>
                     )}
                     {visibleColumns.includes('phone') && (
                       <th 
                         className="text-left py-3 px-4 cursor-pointer hover:bg-gray-100 select-none"
                         onClick={() => handleSort('phone')}
                       >
                         <div className="flex items-center gap-1">
                           Téléphone
                           {sortColumn === 'phone' && (
                             <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                           )}
                         </div>
                       </th>
                     )}
                     {visibleColumns.includes('assignedTo') && (
                       <th 
                         className="text-left py-3 px-4 cursor-pointer hover:bg-gray-100 select-none"
                         onClick={() => handleSort('assignedTo')}
                       >
                         <div className="flex items-center gap-1">
                           Assigné à
                           {sortColumn === 'assignedTo' && (
                             <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                           )}
                         </div>
                       </th>
                     )}
                     {visibleColumns.includes('tags') && (
                       <th 
                         className="text-left py-3 px-4 cursor-pointer hover:bg-gray-100 select-none"
                         onClick={() => handleSort('tags')}
                       >
                         <div className="flex items-center gap-1">
                           Tags
                           {sortColumn === 'tags' && (
                             <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                           )}
                         </div>
                       </th>
                     )}
                     {visibleColumns.includes('billingPeriod') && (
                       <th 
                         className="text-left py-3 px-4 cursor-pointer hover:bg-gray-100 select-none"
                         onClick={() => handleSort('billingPeriod')}
                       >
                         <div className="flex items-center gap-1">
                           Période de facturation
                           {sortColumn === 'billingPeriod' && (
                             <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                           )}
                         </div>
                       </th>
                     )}
                     {visibleColumns.includes('leftToPay') && (
                       <th 
                         className="text-right py-3 px-4 cursor-pointer hover:bg-gray-100 select-none"
                         onClick={() => handleSort('leftToPay')}
                       >
                         <div className="flex items-center gap-1 justify-end">
                           Reste à payer
                           {sortColumn === 'leftToPay' && (
                             <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                           )}
                         </div>
                       </th>
                     )}
                     {visibleColumns.includes('lastOrder') && (
                       <th 
                         className="text-right py-3 px-4 cursor-pointer hover:bg-gray-100 select-none"
                         onClick={() => handleSort('lastOrder')}
                       >
                         <div className="flex items-center gap-1 justify-end">
                           Dernière commande
                           {sortColumn === 'lastOrder' && (
                             <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                           )}
                         </div>
                       </th>
                     )}
                   </tr>
                 </thead>
                 <tbody>
                   {isFullyLoaded
                     ? sortCustomers(customers).map((c: any) => (
                        <tr
               key={c._id}
               className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
               onClick={() => nav(`/customer/${c._id}`)}
               title="Ouvrir la fiche client"
               tabIndex={0}
               onKeyDown={(e) => {
                 if (e.key === 'Enter' || e.key === ' ') {
                   e.preventDefault();
                   nav(`/customer/${c._id}`);
                 }
               }}
             >
                           {visibleColumns.includes('company') && (
                             <td className="py-3 px-4 font-medium text-gray-800">{c.company || c.displayName}</td>
                           )}
                           {visibleColumns.includes('contact') && (
                             <td className="py-3 px-4">{c.contact || '-'}</td>
                           )}
                           {visibleColumns.includes('email') && (
                             <td className="py-3 px-4">{c.mail || '-'}</td>
                           )}
                           {visibleColumns.includes('phone') && (
                             <td className="py-3 px-4">{c.phone || '-'}</td>
                           )}
                           {visibleColumns.includes('assignedTo') && (
                             <td className="py-3 px-4">{getAssignedToName(c)}</td>
                           )}
                           {visibleColumns.includes('tags') && (
                             <td className="py-3 px-4">
                               {(c.tags || c.tag || [])
                                 ?.toString()
                                 ?.split(',')
                                 .filter(Boolean)
                                 .slice(0, 3)
                                 .map((t: string, i: number) => (
                                   <span key={i} className="inline-block mr-1 mb-1 text-[10px] px-2 py-[2px] rounded bg-gray-100">{t.trim()}</span>
                                 ))}
                             </td>
                           )}
                           {visibleColumns.includes('billingPeriod') && (
                             <td className="py-3 px-4">
                               {c.billingPeriod ? (
                                 <span className="text-xs">
                                   {c.billingPeriod === 'weekly' ? 'Hebdomadaire' : 
                                    c.billingPeriod === 'monthly' ? 'Mensuel' :
                                    c.billingPeriod === 'quarterly' ? 'Trimestriel' :
                                    c.billingPeriod}
                                 </span>
                               ) : '-'}
                             </td>
                           )}
                           {visibleColumns.includes('leftToPay') && (
                             <td className="py-3 px-4 text-right">
                               {c.leftToPay !== undefined && c.leftToPay !== null ? (
                                 <span className={`font-medium ${Number(c.leftToPay) > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                                   {Number(c.leftToPay).toFixed(2)}€
                                 </span>
                               ) : '-'}
                             </td>
                           )}
                           {visibleColumns.includes('lastOrder') && (
                             <td className="py-3 px-4 text-right">{getLastOrderDaysAgo(c)}</td>
                           )}
                         </tr>
                       ))
                     : Array(8).fill(0).map((_, i) => (
                         <tr key={i} className="border-t border-gray-100">
                           <td className="py-3 px-4" colSpan={visibleColumns.length}>Chargement...</td>
                         </tr>
                       ))}
                 </tbody>
               </table>
             </div>
           )}
                    </div>
                </div>
                {/* pagination */}
                <Pagination
                    extra="mt-3"
                    maxItems={customersMeta ? customersMeta.total : 12}
                    defaultItemsPerPage={limitPerPage}
                    itemsPerPage={[12, 24, 48]}
                    page={page}
                    onChange={(page: number, limit: number) =>
                        fetchCustomers(page, limit)
                    }
                />
            </Card>
        </>
    );
};

export default CRM;
